import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getDb } from './firebase';
import type { Advertiser } from '../types';

const COLLECTION = 'advertisers';

function toAdvertiser(snap: QueryDocumentSnapshot<DocumentData>): Advertiser {
  const data = snap.data();
  return {
    id: snap.id,
    name: data.name,
    isActive: data.isActive ?? true,
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate().toISOString()
        : (data.createdAt ?? new Date().toISOString()),
    updatedAt:
      data.updatedAt instanceof Timestamp
        ? data.updatedAt.toDate().toISOString()
        : (data.updatedAt ?? new Date().toISOString()),
  };
}

/**
 * Fetch all advertisers ordered alphabetically by name.
 */
export async function getAdvertisers(): Promise<Advertiser[]> {
  const q = query(collection(getDb(), COLLECTION), orderBy('name', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toAdvertiser);
}

/**
 * Create a new advertiser. Returns the generated document ID.
 */
export async function createAdvertiser(
  data: Pick<Advertiser, 'name' | 'isActive'>
): Promise<string> {
  const now = Timestamp.now();
  const docRef = await addDoc(collection(getDb(), COLLECTION), {
    ...data,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

/**
 * Update an existing advertiser's mutable fields.
 */
export async function updateAdvertiser(
  id: string,
  data: Partial<Pick<Advertiser, 'name' | 'isActive'>>
): Promise<void> {
  await updateDoc(doc(getDb(), COLLECTION, id), {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Toggle the isActive state of an advertiser.
 */
export async function toggleAdvertiserActive(id: string, currentValue: boolean): Promise<void> {
  await updateDoc(doc(getDb(), COLLECTION, id), {
    isActive: !currentValue,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete an advertiser document.
 * Caller is responsible for also deleting associated campaigns and storage files.
 */
export async function deleteAdvertiser(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), COLLECTION, id));
}
